import { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { fetchAllVehicles } from '../../lib/vehicleService';
import { Vehicle } from '../../data/vehicles';

const navItems = [
  { name: 'Home', path: '/' },
  { name: 'Inventory', path: '/inventory' },
  { name: 'Financing', path: '/financing' },
  { name: 'About', path: '/about' },
  { name: 'Contact', path: '/contact' },
];

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
  'VA','WA','WV','WI','WY','DC',
];

type FieldErrors = Record<string, string>;

function validate(name: string, value: string): string {
  switch (name) {
    case 'firstName':
    case 'lastName':
      if (!value.trim()) return 'This field is required.';
      if (value.trim().length < 2) return 'Must be at least 2 characters.';
      return '';
    case 'ssn':
      if (!value.trim()) return 'SSN is required.';
      if (!/^\d{3}-\d{2}-\d{4}$/.test(value.trim())) return 'Format must be XXX-XX-XXXX.';
      return '';
    case 'dobMonth':
    case 'dobDay':
    case 'dobYear':
      if (!value) return 'Date of birth is required.';
      return '';
    case 'physicalAddress':
      if (!value.trim()) return 'Address is required.';
      if (value.trim().length < 5) return 'Please enter a valid address.';
      return '';
    case 'zip':
      if (!value.trim()) return 'ZIP code is required.';
      if (!/^\d{5}(-\d{4})?$/.test(value.trim())) return 'Enter a valid ZIP (e.g. 33101).';
      return '';
    case 'city':
      if (!value.trim()) return 'City is required.';
      return '';
    case 'state':
      if (!value) return 'Please select a state.';
      return '';
    case 'cellPhone':
      if (!value.trim()) return 'Cell phone is required.';
      if (!/^[\d\s\(\)\-\+]{10,}$/.test(value.trim())) return 'Enter a valid phone number.';
      return '';
    case 'homePhone':
      if (value.trim() && !/^[\d\s\(\)\-\+]{10,}$/.test(value.trim())) return 'Enter a valid phone number.';
      return '';
    case 'email':
      if (!value.trim()) return 'Email address is required.';
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) return 'Enter a valid email address.';
      return '';
    case 'rentOrOwn':
      if (!value) return 'Please select an option.';
      return '';
    case 'rentMortgage':
      if (!value.trim()) return 'This field is required.';
      if (!/^\$?[\d,]+(\.\d{1,2})?$/.test(value.trim())) return 'Enter a valid dollar amount (e.g. $1,500).';
      return '';
    case 'addressYears':
      if (value === '') return 'Please select years.';
      return '';
    case 'addressMonths':
      if (value === '') return 'Please select months.';
      return '';
    case 'employerName':
      if (!value.trim()) return 'Employer name is required.';
      return '';
    case 'occupation':
      if (!value.trim()) return 'Occupation is required.';
      return '';
    case 'employerYears':
      if (value === '') return 'Please select years.';
      return '';
    case 'employerMonths':
      if (value === '') return 'Please select months.';
      return '';
    case 'employerPhone':
      if (!value.trim()) return 'Phone number is required.';
      if (!/^[\d\s\(\)\-\+]{10,}$/.test(value.trim())) return 'Enter a valid phone number.';
      return '';
    case 'monthlyIncome':
      if (!value.trim()) return 'Monthly income is required.';
      if (!/^\$?[\d,]+(\.\d{1,2})?$/.test(value.trim())) return 'Enter a valid dollar amount (e.g. $5,000).';
      return '';
    default:
      return '';
  }
}

const REQUIRED_FIELDS = [
  'firstName','lastName','ssn','dobMonth','dobDay','dobYear',
  'physicalAddress','zip','city','state','cellPhone','email',
  'rentOrOwn','rentMortgage','addressYears','addressMonths',
  'employerName','occupation','employerYears','employerMonths',
  'employerPhone','monthlyIncome',
];

function ErrorMsg({ msg }: { msg: string }) {
  if (!msg) return null;
  return (
    <p className="flex items-center gap-1.5 mt-1.5 text-[11px] text-red-600 font-medium" style={{ fontFamily: 'Montserrat, sans-serif' }}>
      <i className="ri-error-warning-fill text-sm flex-shrink-0"></i>
      {msg}
    </p>
  );
}

export default function FinancingPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submittedName, setSubmittedName] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [errors, setErrors] = useState<FieldErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [vehicleOfInterest, setVehicleOfInterest] = useState('');
  const [downPayment, setDownPayment] = useState('');
  const [searchParams] = useSearchParams();
  const [showCoApplicant, setShowCoApplicant] = useState(false);
  const [currentSection, setCurrentSection] = useState('Vehicle');
  const [showTradeIn, setShowTradeIn] = useState(false);
  const [vehicleSuggestions, setVehicleSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);
  const vehicleInputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const [liveVehicles, setLiveVehicles] = useState<Vehicle[]>([]);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Pre-fill vehicle of interest from URL query param
  useEffect(() => {
    const vehicleParam = searchParams.get('vehicle');
    if (vehicleParam) {
      setVehicleOfInterest(decodeURIComponent(vehicleParam));
    }
  }, [searchParams]);

  // Track current section on scroll for mobile progress indicator
  useEffect(() => {
    const handleSectionScroll = () => {
      const sections = ['vehicle', 'personal', 'residential', 'employment', 'co-applicant', 'consent'];
      const sectionElements = sections.map(id => document.getElementById(`section-${id}`));
      
      for (let i = sectionElements.length - 1; i >= 0; i--) {
        const el = sectionElements[i];
        if (el && el.getBoundingClientRect().top <= 200) {
          const sectionNames = ['Vehicle', 'Personal', 'Residential', 'Employment', 'Co-Applicant', 'Consent'];
          setCurrentSection(sectionNames[i]);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleSectionScroll);
    return () => window.removeEventListener('scroll', handleSectionScroll);
  }, []);

  // Fetch live vehicles for autocomplete
  useEffect(() => {
    fetchAllVehicles()
      .then(setLiveVehicles)
      .catch(console.error);
  }, []);

  // Filter vehicle suggestions when user types
  useEffect(() => {
    if (vehicleOfInterest.length >= 3) {
      const query = vehicleOfInterest.toLowerCase();
      const available = liveVehicles.filter(v => !v.sold);
      const matches = available
        .filter(v => {
          const fullName = `${v.year} ${v.name}`.toLowerCase();
          return fullName.includes(query);
        })
        .map(v => `${v.year} ${v.name}`)
        .filter((value, index, self) => self.indexOf(value) === index) // deduplicate
        .slice(0, 8);
      setVehicleSuggestions(matches);
      setShowSuggestions(matches.length > 0);
      setActiveSuggestionIndex(-1);
    } else {
      setVehicleSuggestions([]);
      setShowSuggestions(false);
    }
  }, [vehicleOfInterest, liveVehicles]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        vehicleInputRef.current && !vehicleInputRef.current.contains(e.target as Node) &&
        suggestionsRef.current && !suggestionsRef.current.contains(e.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Schema.org JSON-LD injection
  useEffect(() => {
    const baseUrl = import.meta.env.VITE_SITE_URL || 'https://paramountautosusa.com';
    
    const schemaData = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": `${baseUrl}/financing#webpage`,
          "url": `${baseUrl}/financing`,
          "name": "Auto Financing Application - Paramount Motors",
          "description": "Apply for auto financing at Paramount Motors. Fast approval, competitive rates, and flexible terms for luxury and exotic vehicle purchases.",
          "isPartOf": {
            "@id": `${baseUrl}/#website`
          },
          "about": {
            "@id": `${baseUrl}/financing#financialproduct`
          }
        },
        {
          "@type": "FinancialProduct",
          "@id": `${baseUrl}/financing#financialproduct`,
          "name": "Auto Financing",
          "description": "Flexible auto financing options for luxury and exotic vehicles. Competitive rates, fast approval, and personalized service from our financing team.",
          "provider": {
            "@id": `${baseUrl}/#organization`
          },
          "feesAndCommissionsSpecification": "Rates and terms vary based on credit approval. Contact us for personalized quotes.",
          "interestRate": {
            "@type": "QuantitativeValue",
            "minValue": 3.99,
            "maxValue": 12.99,
            "unitText": "Percent"
          }
        },
        {
          "@type": "AutoDealer",
          "@id": `${baseUrl}/#organization`,
          "name": "Paramount Motors",
          "url": baseUrl
        }
      ]
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schemaData);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const handleVehicleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggestions) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveSuggestionIndex(prev => Math.min(prev + 1, vehicleSuggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveSuggestionIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && activeSuggestionIndex >= 0) {
      e.preventDefault();
      setVehicleOfInterest(vehicleSuggestions[activeSuggestionIndex]);
      setShowSuggestions(false);
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  const handleBlur = (name: string, value: string) => {
    setTouched(prev => ({ ...prev, [name]: true }));
    const err = validate(name, value);
    setErrors(prev => ({ ...prev, [name]: err }));
  };

  const handleChange = (name: string, value: string) => {
    if (touched[name]) {
      const err = validate(name, value);
      setErrors(prev => ({ ...prev, [name]: err }));
    }
  };

  const formatSSN = (value: string): string => {
    const digits = value.replace(/\D/g, '').slice(0, 9);
    if (digits.length <= 3) return digits;
    if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
  };

  const formatPhone = (value: string): string => {
    const digits = value.replace(/\D/g, '').slice(0, 10);
    if (digits.length <= 3) return digits.length ? `(${digits}` : '';
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>, fieldName: string) => {
    const formatted = formatPhone(e.target.value);
    e.target.value = formatted;
    handleChange(fieldName, formatted);
  };

  const handleSSNChange = (e: React.ChangeEvent<HTMLInputElement>, fieldName: string) => {
    const formatted = formatSSN(e.target.value);
    e.target.value = formatted;
    handleChange(fieldName, formatted);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!agreed) {
      setSubmitError('You must agree to the terms before submitting.');
      return;
    }

    const form = e.currentTarget;
    const newErrors: FieldErrors = {};
    const newTouched: Record<string, boolean> = {};

    REQUIRED_FIELDS.forEach(fieldName => {
      const el = form.elements.namedItem(fieldName) as HTMLInputElement | HTMLSelectElement | null;
      const val = el ? el.value : '';
      newTouched[fieldName] = true;
      const err = validate(fieldName, val);
      if (err) newErrors[fieldName] = err;
    });

    setTouched(prev => ({ ...prev, ...newTouched }));
    setErrors(prev => ({ ...prev, ...newErrors }));

    if (Object.keys(newErrors).length > 0) {
      setSubmitError('Please fix the errors above before submitting.');
      const firstErrorField = REQUIRED_FIELDS.find(f => newErrors[f]);
      if (firstErrorField) {
        const el = form.elements.namedItem(firstErrorField) as HTMLElement | null;
        el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el?.focus();
      }
      return;
    }

    // Capture full name before reset
    const firstNameEl = form.elements.namedItem('firstName') as HTMLInputElement | null;
    const lastNameEl = form.elements.namedItem('lastName') as HTMLInputElement | null;
    const fullName = `${firstNameEl?.value || ''} ${lastNameEl?.value || ''}`.trim();

    setSubmitError('');
    setIsSubmitting(true);
    const data = new URLSearchParams();
    Array.from(form.elements).forEach((el) => {
      const input = el as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
      if (input.name && input.value && input.value.trim() !== '') {
        data.append(input.name, input.value.trim());
      }
    });
    try {
      await fetch('https://readdy.ai/api/form/d6n0agg5dsf7sr1n6h8g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      setSubmittedName(fullName);
      setSubmitSuccess(true);
      form.reset();
      setAgreed(false);
      setErrors({});
      setTouched({});
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setSubmitError('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Mobile-optimized field classes with larger tap targets and font size
  const fieldClass = (name: string) =>
    `w-full px-4 py-3.5 min-h-[48px] bg-white border ${touched[name] && errors[name] ? 'border-red-400 focus:border-red-500 focus:ring-2 focus:ring-red-200' : 'border-[#d1c9b8] focus:border-[#C9A84C] focus:ring-2 focus:ring-[#C9A84C]/20'} rounded-md text-base text-[#1a1a1a] placeholder-[#b0a898] focus:outline-none transition-all`;

  const selectClass = (name: string) => `${fieldClass(name)} appearance-none cursor-pointer`;
  const labelClass = 'block text-xs font-semibold text-[#4a4035] mb-2 tracking-wider uppercase';

  const SectionTitle = ({ title }: { title: string }) => (
    <div className="flex items-center gap-3 mb-5">
      <div className="h-px flex-1 bg-[#e0d8cc]"></div>
      <span className="text-[#C9A84C] text-[10px] tracking-[0.3em] uppercase" style={{ fontFamily: 'Montserrat, sans-serif' }}>{title}</span>
      <div className="h-px flex-1 bg-[#e0d8cc]"></div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0d0d0d]">
      <title>Auto Financing | Paramount Autos USA – Fast Approval Miami, FL</title>
      <meta name="description" content="Apply for auto financing at Paramount Autos USA in Miami, FL. Competitive rates, fast approval, and flexible terms for luxury, trucks, and SUVs. Serving all of the USA." />
      <link rel="canonical" href="https://paramountautosusa.com/financing" />
      {/* Navbar */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${isScrolled ? 'bg-[#0d0d0d]/95 backdrop-blur-md shadow-lg shadow-black/30 border-b border-[#C9A84C]/20 py-3 sm:py-4' : 'bg-[#0d0d0d] py-4 sm:py-5'}`}>
        <div className="w-full px-4 sm:px-8 lg:px-16">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 sm:gap-3 cursor-pointer">
              <img
                src="https://static.readdy.ai/image/41574e0492e980fd88e2c6f8d59234d9/7d1f372df28459c5713c0f3e160d8f18.png"
                alt="Paramount Motors Logo"
                style={{ height: '5rem', width: 'auto' }}
                className="object-contain"
              />
            </Link>
            <div className="hidden lg:flex items-center gap-8">
              {navItems.map((item) => (
                <Link key={item.name} to={item.path} className={`text-xs font-medium tracking-widest uppercase transition-all cursor-pointer relative ${item.name === 'Financing' ? 'text-[#C9A84C]' : 'text-white/60 hover:text-[#C9A84C]'}`} style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  {item.name}
                  {item.name === 'Financing' && <span className="absolute -bottom-1 left-0 w-full h-px bg-[#C9A84C]"></span>}
                </Link>
              ))}
            </div>
            <Link to="/financing" className="hidden lg:block px-6 py-2.5 rounded-sm text-xs font-bold tracking-widest uppercase bg-[#C9A84C] text-[#0d0d0d] hover:bg-[#e0bc5e] transition-all cursor-pointer whitespace-nowrap" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Financing
            </Link>
            <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="lg:hidden w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center cursor-pointer text-white">
              <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl sm:text-2xl`}></i>
            </button>
          </div>
          {isMobileMenuOpen && (
            <div className="lg:hidden mt-4 sm:mt-6 pb-4 sm:pb-6 border-t border-white/10 pt-4 sm:pt-6">
              <div className="flex flex-col gap-4 sm:gap-5">
                {navItems.map((item) => (
                  <Link key={item.name} to={item.path} onClick={() => setIsMobileMenuOpen(false)} className="text-xs font-medium tracking-widest uppercase text-white/60 hover:text-[#C9A84C] transition-colors cursor-pointer" style={{ fontFamily: 'Montserrat, sans-serif' }}>{item.name}</Link>
                ))}
                <Link to="/financing" onClick={() => setIsMobileMenuOpen(false)} className="mt-2 px-6 py-3 rounded-sm text-xs font-semibold tracking-widest uppercase bg-[#C9A84C] text-[#0d0d0d] cursor-pointer whitespace-nowrap text-center">
                  Financing
                </Link>
              </div>
            </div>
          )}
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#C9A84C]/50 to-transparent"></div>
      </nav>

      {/* Sticky Mobile Progress Indicator */}
      <div className="lg:hidden fixed top-[60px] left-0 right-0 z-40 bg-[#0d0d0d]/95 backdrop-blur-md border-b border-[#C9A84C]/20 px-4 py-2.5">
        <div className="flex items-center gap-2">
          <i className="ri-file-list-3-line text-[#C9A84C] text-sm"></i>
          <span className="text-[10px] text-white/40 tracking-widest uppercase" style={{ fontFamily: 'Montserrat, sans-serif' }}>Current Section:</span>
          <span className="text-xs text-[#C9A84C] font-bold tracking-wider" style={{ fontFamily: 'Montserrat, sans-serif' }}>{currentSection}</span>
        </div>
      </div>

      {/* Hero Header - Simplified for mobile */}
      <section className="relative pt-20 pb-4 sm:pt-28 sm:pb-6 md:pt-32 md:pb-8 px-4 sm:px-8 lg:px-16 overflow-hidden">
        <div className="absolute inset-0 hidden sm:block">
          <img
            src="https://public.readdy.ai/ai/img_res/e1138a5eb7a233592544bf75d3e8fca1.jpg"
            alt="Financing"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0d0d0d]/80 via-[#0d0d0d]/70 to-[#0d0d0d]"></div>
        </div>
      </section>

      {/* Application Form */}
      <section id="application-form" className="py-6 sm:py-10 md:py-14 lg:py-16 px-4 sm:px-8 lg:px-16 bg-[#0d0d0d]">
        <div className="max-w-4xl mx-auto w-full">
          <div className="text-center mb-6 sm:mb-10">
            <div className="flex items-center justify-center gap-3 sm:gap-4 mb-2 sm:mb-3">
              <div className="h-px w-8 sm:w-12 bg-[#C9A84C]"></div>
              <span className="text-[#C9A84C] text-[10px] sm:text-xs tracking-[0.3em] sm:tracking-[0.4em] uppercase" style={{ fontFamily: 'Montserrat, sans-serif' }}>Apply Now</span>
              <div className="h-px w-8 sm:w-12 bg-[#C9A84C]"></div>
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Apply for Financing</h2>
            <p className="text-white/40 text-sm sm:text-sm mt-2 px-4" style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 300 }}>Our financing team will contact you within 24 hours.</p>
          </div>

          {submitSuccess ? (
            <div className="relative overflow-hidden rounded-2xl border border-[#C9A84C]/30 shadow-2xl">
              {/* Background image with overlay */}
              <div className="absolute inset-0">
                <img
                  src="https://readdy.ai/api/search-image?query=luxury%20car%20keys%20on%20a%20polished%20dark%20surface%20with%20soft%20golden%20bokeh%20lights%20in%20the%20background%2C%20elegant%20premium%20automotive%20atmosphere%2C%20dark%20moody%20tones%20with%20warm%20gold%20accents%2C%20minimalist%20composition&width=900&height=600&seq=success-bg-01&orientation=landscape"
                  alt=""
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-br from-[#0d0d0d]/95 via-[#0d0d0d]/85 to-[#1a1408]/90"></div>
              </div>

              {/* Content */}
              <div className="relative z-10 flex flex-col items-center text-center px-6 py-12 sm:px-8 sm:py-16 md:py-20">
                {/* Animated checkmark ring */}
                <div className="relative mb-6 sm:mb-8">
                  <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full border-2 border-[#C9A84C]/30 flex items-center justify-center">
                    <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full border-2 border-[#C9A84C] flex items-center justify-center bg-[#C9A84C]/10">
                      <i className="ri-check-line text-3xl sm:text-4xl text-[#C9A84C]"></i>
                    </div>
                  </div>
                  <div className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-[#C9A84C]/60"></div>
                  <div className="absolute -bottom-2 -left-2 w-2 h-2 rounded-full bg-[#C9A84C]/40"></div>
                </div>

                <div className="flex items-center gap-3 mb-4 sm:mb-6">
                  <div className="h-px w-10 bg-[#C9A84C]/50"></div>
                  <i className="ri-shield-star-fill text-[#C9A84C] text-sm"></i>
                  <div className="h-px w-10 bg-[#C9A84C]/50"></div>
                </div>

                <p className="text-[#C9A84C] text-[10px] tracking-[0.4em] uppercase mb-2 sm:mb-3" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  Application Received
                </p>

                <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-2 leading-tight" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  Thank You,
                </h3>
                <h4 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#C9A84C] mb-4 sm:mb-6 leading-tight" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  {submittedName}
                </h4>

                <p className="text-white/60 text-sm sm:text-base max-w-md leading-relaxed mb-2 sm:mb-3 px-4" style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 300 }}>
                  Your financing application has been successfully submitted to our team.
                </p>
                <p className="text-white/40 text-xs sm:text-sm max-w-sm leading-relaxed mb-8 sm:mb-10 px-4" style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 300 }}>
                  A specialist will review your application and reach out within <span className="text-[#C9A84C] font-medium">24 hours</span> to discuss your options.
                </p>

                {/* Action buttons */}
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4 w-full sm:w-auto px-4 sm:px-0">
                  <button
                    onClick={() => { setSubmitSuccess(false); setSubmittedName(''); }}
                    className="w-full sm:w-auto px-6 sm:px-8 py-3.5 sm:py-3 rounded-md text-xs font-bold tracking-widest uppercase bg-[#C9A84C] text-[#0d0d0d] hover:bg-[#e0bc5e] transition-all cursor-pointer whitespace-nowrap shadow-lg"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  >
                    Submit Another Application
                  </button>
                  <Link
                    to="/inventory"
                    className="w-full sm:w-auto px-6 sm:px-8 py-3.5 sm:py-3 rounded-md text-xs font-bold tracking-widest uppercase border border-white/20 text-white/60 hover:border-[#C9A84C]/50 hover:text-[#C9A84C] transition-all cursor-pointer whitespace-nowrap text-center"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  >
                    Browse Inventory
                  </Link>
                </div>
              </div>
            </div>
          ) : (
            <form id="financing-application-form" data-readdy-form onSubmit={handleSubmit} noValidate className="bg-white border border-[#e0d8cc] rounded-lg p-4 sm:p-6 md:p-8 lg:p-10 space-y-8 sm:space-y-10 shadow-lg">

              {/* ── Vehicle of Interest ── */}
              <div id="section-vehicle">
                <SectionTitle title="Vehicle of Interest" />
                <div className="space-y-4">
                  <div className="relative">
                    <i className="ri-car-line absolute left-4 top-1/2 -translate-y-1/2 text-[#b0a898] text-base z-10"></i>
                    <input
                      ref={vehicleInputRef}
                      type="text"
                      name="vehicleOfInterest"
                      value={vehicleOfInterest}
                      onChange={(e) => setVehicleOfInterest(e.target.value)}
                      onKeyDown={handleVehicleKeyDown}
                      onFocus={() => vehicleOfInterest.length >= 3 && vehicleSuggestions.length > 0 && setShowSuggestions(true)}
                      className="w-full pl-11 pr-4 py-3.5 min-h-[48px] bg-white border border-[#d1c9b8] focus:border-[#C9A84C] focus:ring-2 focus:ring-[#C9A84C]/20 rounded-md text-base text-[#1a1a1a] placeholder-[#b0a898] focus:outline-none transition-all"
                      placeholder="e.g., 2024 BMW M4 Competition"
                      style={{ fontFamily: 'Montserrat, sans-serif' }}
                      autoComplete="off"
                    />
                    {/* Autocomplete Dropdown */}
                    {showSuggestions && (
                      <div
                        ref={suggestionsRef}
                        className="absolute top-full left-0 right-0 mt-1 bg-white border border-[#d1c9b8] rounded-md shadow-xl z-50 overflow-hidden"
                      >
                        <div className="flex items-center gap-2 px-4 py-2 bg-[#f9f7f4] border-b border-[#e0d8cc]">
                          <i className="ri-search-line text-[#C9A84C] text-xs"></i>
                          <span className="text-[10px] text-[#6b5e4e] tracking-widest uppercase font-semibold" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                            Matching Inventory ({vehicleSuggestions.length})
                          </span>
                        </div>
                        {vehicleSuggestions.map((suggestion, index) => (
                          <div
                            key={suggestion}
                            onMouseDown={() => {
                              setVehicleOfInterest(suggestion);
                              setShowSuggestions(false);
                            }}
                            className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-all border-b border-[#f0ece4] last:border-0 ${
                              index === activeSuggestionIndex
                                ? 'bg-[#C9A84C]/10 text-[#1a1a1a]'
                                : 'hover:bg-[#fdf8ee] text-[#1a1a1a]'
                            }`}
                            onMouseEnter={() => setActiveSuggestionIndex(index)}
                          >
                            <div className="w-7 h-7 flex items-center justify-center bg-[#f0ece4] rounded-full flex-shrink-0">
                              <i className="ri-car-line text-[#C9A84C] text-sm"></i>
                            </div>
                            <span className="text-sm" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                              {suggestion}
                            </span>
                            <i className="ri-arrow-right-up-line text-[#b0a898] text-xs ml-auto"></i>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <p className="text-[#6b5e4e] text-xs mt-1 ml-1" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                    {vehicleOfInterest.length > 0 && vehicleOfInterest.length < 3
                      ? `Type ${3 - vehicleOfInterest.length} more character${3 - vehicleOfInterest.length > 1 ? 's' : ''} to search inventory...`
                      : 'Optional: Type to search our inventory or enter any vehicle name'}
                  </p>

                  {/* Down Payment */}
                  <div>
                    <label className={labelClass} style={{ fontFamily: 'Montserrat, sans-serif' }}>Down Payment</label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#b0a898] text-base font-medium pointer-events-none">$</span>
                      <input
                        type="text"
                        name="downPayment"
                        value={downPayment}
                        onChange={(e) => setDownPayment(e.target.value)}
                        className="w-full pl-8 pr-4 py-3.5 min-h-[48px] bg-white border border-[#d1c9b8] focus:border-[#C9A84C] focus:ring-2 focus:ring-[#C9A84C]/20 rounded-md text-base text-[#1a1a1a] placeholder-[#b0a898] focus:outline-none transition-all"
                        placeholder="e.g., 5,000"
                        style={{ fontFamily: 'Montserrat, sans-serif' }}
                      />
                    </div>
                    <p className="text-[#6b5e4e] text-xs mt-1.5 ml-1" style={{ fontFamily: 'Montserrat, sans-serif' }}>Optional: Amount you plan to put down upfront</p>
                  </div>
                </div>
              </div>

              {/* ── Personal Information ── */}
              <div id="section-personal">
                <SectionTitle title="Personal Information" />
                <div className="space-y-4">
                  {/* First Name + MI + Last Name */}
                  <div className="grid grid-cols-1 sm:grid-cols-[1fr_60px_1fr] gap-3">
                    <div>
                      <label className={labelClass} style={{ fontFamily: 'Montserrat, sans-serif' }}>First Name <span className="text-red-500">*</span></label>
                      <input
                        type="text" name="firstName"
                        className={fieldClass('firstName')}
                        placeholder="John"
                        style={{ fontFamily: 'Montserrat, sans-serif' }}
                        onBlur={(e) => handleBlur('firstName', e.target.value)}
                        onChange={(e) => handleChange('firstName', e.target.value)}
                      />
                      <ErrorMsg msg={touched.firstName ? errors.firstName || '' : ''} />
                    </div>
                    <div>
                      <label className={labelClass} style={{ fontFamily: 'Montserrat, sans-serif' }}>MI</label>
                      <input type="text" name="middleInitial" maxLength={1} className={fieldClass('middleInitial')} placeholder="A" style={{ fontFamily: 'Montserrat, sans-serif' }} />
                    </div>
                    <div>
                      <label className={labelClass} style={{ fontFamily: 'Montserrat, sans-serif' }}>Last Name <span className="text-red-500">*</span></label>
                      <input
                        type="text" name="lastName"
                        className={fieldClass('lastName')}
                        placeholder="Doe"
                        style={{ fontFamily: 'Montserrat, sans-serif' }}
                        onBlur={(e) => handleBlur('lastName', e.target.value)}
                        onChange={(e) => handleChange('lastName', e.target.value)}
                      />
                      <ErrorMsg msg={touched.lastName ? errors.lastName || '' : ''} />
                    </div>
                  </div>

                  {/* SSN + DOB */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className={labelClass} style={{ fontFamily: 'Montserrat, sans-serif' }}>Social Security Number <span className="text-red-500">*</span></label>
                      <input
                        type="text" name="ssn"
                        className={fieldClass('ssn')}
                        placeholder="XXX-XX-XXXX"
                        maxLength={11}
                        style={{ fontFamily: 'Montserrat, sans-serif' }}
                        onChange={(e) => handleSSNChange(e, 'ssn')}
                        onBlur={(e) => handleBlur('ssn', e.target.value)}
                      />
                      <ErrorMsg msg={touched.ssn ? errors.ssn || '' : ''} />
                    </div>
                    <div>
                      <label className={labelClass} style={{ fontFamily: 'Montserrat, sans-serif' }}>Date of Birth <span className="text-red-500">*</span></label>
                      <div className="grid grid-cols-3 gap-1.5">
                        <div className="relative">
                          <select name="dobMonth" className={`${selectClass('dobMonth')} pr-6 text-sm`} style={{ fontFamily: 'Montserrat, sans-serif' }} onBlur={(e) => handleBlur('dobMonth', e.target.value)} onChange={(e) => handleChange('dobMonth', e.target.value)}>
                            <option value="">MM</option>
                            {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                              <option key={m} value={String(m).padStart(2, '0')}>{String(m).padStart(2, '0')}</option>
                            ))}
                          </select>
                          <i className="ri-arrow-down-s-line absolute right-1.5 top-1/2 -translate-y-1/2 text-[#b0a898] pointer-events-none text-sm"></i>
                        </div>
                        <div className="relative">
                          <select name="dobDay" className={`${selectClass('dobDay')} pr-6 text-sm`} style={{ fontFamily: 'Montserrat, sans-serif' }} onBlur={(e) => handleBlur('dobDay', e.target.value)} onChange={(e) => handleChange('dobDay', e.target.value)}>
                            <option value="">DD</option>
                            {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                              <option key={d} value={String(d).padStart(2, '0')}>{String(d).padStart(2, '0')}</option>
                            ))}
                          </select>
                          <i className="ri-arrow-down-s-line absolute right-1.5 top-1/2 -translate-y-1/2 text-[#b0a898] pointer-events-none text-sm"></i>
                        </div>
                        <div className="relative">
                          <select name="dobYear" className={`${selectClass('dobYear')} pr-6 text-sm`} style={{ fontFamily: 'Montserrat, sans-serif' }} onBlur={(e) => handleBlur('dobYear', e.target.value)} onChange={(e) => handleChange('dobYear', e.target.value)}>
                            <option value="">YYYY</option>
                            {Array.from({ length: 80 }, (_, i) => new Date().getFullYear() - 18 - i).map((y) => (
                              <option key={y} value={y}>{y}</option>
                            ))}
                          </select>
                          <i className="ri-arrow-down-s-line absolute right-1.5 top-1/2 -translate-y-1/2 text-[#b0a898] pointer-events-none text-sm"></i>
                        </div>
                      </div>
                      <ErrorMsg msg={touched.dobMonth ? errors.dobMonth || '' : ''} />
                    </div>
                  </div>

                  {/* Cell Phone + Home Phone */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className={labelClass} style={{ fontFamily: 'Montserrat, sans-serif' }}>Cell Phone <span className="text-red-500">*</span></label>
                      <input
                        type="tel" name="cellPhone"
                        className={fieldClass('cellPhone')}
                        placeholder="(305) 000-0000"
                        style={{ fontFamily: 'Montserrat, sans-serif' }}
                        onChange={(e) => handlePhoneChange(e, 'cellPhone')}
                        onBlur={(e) => handleBlur('cellPhone', e.target.value)}
                      />
                      <ErrorMsg msg={touched.cellPhone ? errors.cellPhone || '' : ''} />
                    </div>
                    <div>
                      <label className={labelClass} style={{ fontFamily: 'Montserrat, sans-serif' }}>Home Phone</label>
                      <input
                        type="tel" name="homePhone"
                        className={fieldClass('homePhone')}
                        placeholder="(305) 000-0000"
                        style={{ fontFamily: 'Montserrat, sans-serif' }}
                        onChange={(e) => handlePhoneChange(e, 'homePhone')}
                        onBlur={(e) => handleBlur('homePhone', e.target.value)}
                      />
                      <ErrorMsg msg={touched.homePhone ? errors.homePhone || '' : ''} />
                    </div>
                  </div>

                  {/* Email */}
                  <div>
                    <label className={labelClass} style={{ fontFamily: 'Montserrat, sans-serif' }}>E-Mail Address <span className="text-red-500">*</span></label>
                    <input
                      type="email" name="email"
                      className={fieldClass('email')}
                      placeholder="john@example.com"
                      style={{ fontFamily: 'Montserrat, sans-serif' }}
                      onBlur={(e) => handleBlur('email', e.target.value)}
                      onChange={(e) => handleChange('email', e.target.value)}
                    />
                    <ErrorMsg msg={touched.email ? errors.email || '' : ''} />
                  </div>
                </div>
              </div>

              {/* ── Residential Information ── */}
              <div id="section-residential">
                <SectionTitle title="Residential Information" />
                <div className="space-y-4">
                  {/* Physical Address */}
                  <div>
                    <label className={labelClass} style={{ fontFamily: 'Montserrat, sans-serif' }}>Physical Address <span className="text-red-500">*</span></label>
                    <input
                      type="text" name="physicalAddress"
                      className={fieldClass('physicalAddress')}
                      placeholder="123 Main Street"
                      style={{ fontFamily: 'Montserrat, sans-serif' }}
                      onBlur={(e) => handleBlur('physicalAddress', e.target.value)}
                      onChange={(e) => handleChange('physicalAddress', e.target.value)}
                    />
                    <ErrorMsg msg={touched.physicalAddress ? errors.physicalAddress || '' : ''} />
                  </div>

                  {/* City + State + ZIP */}
                  <div className="grid grid-cols-1 sm:grid-cols-[1fr_100px_120px] gap-3">
                    <div>
                      <label className={labelClass} style={{ fontFamily: 'Montserrat, sans-serif' }}>City <span className="text-red-500">*</span></label>
                      <input
                        type="text" name="city"
                        className={fieldClass('city')}
                        placeholder="Miami"
                        style={{ fontFamily: 'Montserrat, sans-serif' }}
                        onBlur={(e) => handleBlur('city', e.target.value)}
                        onChange={(e) => handleChange('city', e.target.value)}
                      />
                      <ErrorMsg msg={touched.city ? errors.city || '' : ''} />
                    </div>
                    <div>
                      <label className={labelClass} style={{ fontFamily: 'Montserrat, sans-serif' }}>State <span className="text-red-500">*</span></label>
                      <div className="relative">
                        <select
                          name="state"
                          className={selectClass('state')}
                          style={{ fontFamily: 'Montserrat, sans-serif' }}
                          onBlur={(e) => handleBlur('state', e.target.value)}
                          onChange={(e) => handleChange('state', e.target.value)}
                        >
                          <option value="">--</option>
                          {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                        <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-[#b0a898] pointer-events-none text-sm"></i>
                      </div>
                      <ErrorMsg msg={touched.state ? errors.state || '' : ''} />
                    </div>
                    <div>
                      <label className={labelClass} style={{ fontFamily: 'Montserrat, sans-serif' }}>ZIP Code <span className="text-red-500">*</span></label>
                      <input
                        type="text" name="zip"
                        className={fieldClass('zip')}
                        placeholder="33101"
                        maxLength={10}
                        style={{ fontFamily: 'Montserrat, sans-serif' }}
                        onBlur={(e) => handleBlur('zip', e.target.value)}
                        onChange={(e) => handleChange('zip', e.target.value)}
                      />
                      <ErrorMsg msg={touched.zip ? errors.zip || '' : ''} />
                    </div>
                  </div>

                  {/* Rent or Own + Monthly Payment */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className={labelClass} style={{ fontFamily: 'Montserrat, sans-serif' }}>Rent or Own <span className="text-red-500">*</span></label>
                      <div className="relative">
                        <select
                          name="rentOrOwn"
                          className={selectClass('rentOrOwn')}
                          style={{ fontFamily: 'Montserrat, sans-serif' }}
                          onBlur={(e) => handleBlur('rentOrOwn', e.target.value)}
                          onChange={(e) => handleChange('rentOrOwn', e.target.value)}
                        >
                          <option value="">Select...</option>
                          <option value="Own">Own</option>
                          <option value="Rent">Rent</option>
                          <option value="Other">Other</option>
                        </select>
                        <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-[#b0a898] pointer-events-none text-sm"></i>
                      </div>
                      <ErrorMsg msg={touched.rentOrOwn ? errors.rentOrOwn || '' : ''} />
                    </div>
                    <div>
                      <label className={labelClass} style={{ fontFamily: 'Montserrat, sans-serif' }}>Monthly Rent / Mortgage <span className="text-red-500">*</span></label>
                      <div className="relative">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#b0a898] text-base font-medium pointer-events-none">$</span>
                        <input
                          type="text" name="rentMortgage"
                          className={`${fieldClass('rentMortgage')} pl-8`}
                          placeholder="1,500"
                          style={{ fontFamily: 'Montserrat, sans-serif' }}
                          onBlur={(e) => handleBlur('rentMortgage', e.target.value)}
                          onChange={(e) => handleChange('rentMortgage', e.target.value)}
                        />
                      </div>
                      <ErrorMsg msg={touched.rentMortgage ? errors.rentMortgage || '' : ''} />
                    </div>
                  </div>

                  {/* Time at Address */}
                  <div>
                    <label className={labelClass} style={{ fontFamily: 'Montserrat, sans-serif' }}>Time at Current Address <span className="text-red-500">*</span></label>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="relative">
                        <select
                          name="addressYears"
                          className={selectClass('addressYears')}
                          style={{ fontFamily: 'Montserrat, sans-serif' }}
                          onBlur={(e) => handleBlur('addressYears', e.target.value)}
                          onChange={(e) => handleChange('addressYears', e.target.value)}
                        >
                          <option value="">Years</option>
                          {Array.from({ length: 31 }, (_, i) => i).map((y) => (
                            <option key={y} value={y}>{y} {y === 1 ? 'Year' : 'Years'}</option>
                          ))}
                        </select>
                        <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-[#b0a898] pointer-events-none text-sm"></i>
                      </div>
                      <div className="relative">
                        <select
                          name="addressMonths"
                          className={selectClass('addressMonths')}
                          style={{ fontFamily: 'Montserrat, sans-serif' }}
                          onBlur={(e) => handleBlur('addressMonths', e.target.value)}
                          onChange={(e) => handleChange('addressMonths', e.target.value)}
                        >
                          <option value="">Months</option>
                          {Array.from({ length: 12 }, (_, i) => i).map((m) => (
                            <option key={m} value={m}>{m} {m === 1 ? 'Month' : 'Months'}</option>
                          ))}
                        </select>
                        <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-[#b0a898] pointer-events-none text-sm"></i>
                      </div>
                    </div>
                    <ErrorMsg msg={touched.addressYears ? errors.addressYears || '' : ''} />
                  </div>
                </div>
              </div>

              {/* ── Employment Information ── */}
              <div id="section-employment">
                <SectionTitle title="Employment Information" />
                <div className="space-y-4">
                  {/* Employer + Occupation */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className={labelClass} style={{ fontFamily: 'Montserrat, sans-serif' }}>Employer Name <span className="text-red-500">*</span></label>
                      <input
                        type="text" name="employerName"
                        className={fieldClass('employerName')}
                        placeholder="Acme Corporation"
                        style={{ fontFamily: 'Montserrat, sans-serif' }}
                        onBlur={(e) => handleBlur('employerName', e.target.value)}
                        onChange={(e) => handleChange('employerName', e.target.value)}
                      />
                      <ErrorMsg msg={touched.employerName ? errors.employerName || '' : ''} />
                    </div>
                    <div>
                      <label className={labelClass} style={{ fontFamily: 'Montserrat, sans-serif' }}>Occupation <span className="text-red-500">*</span></label>
                      <input
                        type="text" name="occupation"
                        className={fieldClass('occupation')}
                        placeholder="Sales Manager"
                        style={{ fontFamily: 'Montserrat, sans-serif' }}
                        onBlur={(e) => handleBlur('occupation', e.target.value)}
                        onChange={(e) => handleChange('occupation', e.target.value)}
                      />
                      <ErrorMsg msg={touched.occupation ? errors.occupation || '' : ''} />
                    </div>
                  </div>

                  {/* Employer Phone + Monthly Income */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className={labelClass} style={{ fontFamily: 'Montserrat, sans-serif' }}>Employer Phone <span className="text-red-500">*</span></label>
                      <input
                        type="tel" name="employerPhone"
                        className={fieldClass('employerPhone')}
                        placeholder="(305) 000-0000"
                        style={{ fontFamily: 'Montserrat, sans-serif' }}
                        onChange={(e) => handlePhoneChange(e, 'employerPhone')}
                        onBlur={(e) => handleBlur('employerPhone', e.target.value)}
                      />
                      <ErrorMsg msg={touched.employerPhone ? errors.employerPhone || '' : ''} />
                    </div>
                    <div>
                      <label className={labelClass} style={{ fontFamily: 'Montserrat, sans-serif' }}>Monthly Income <span className="text-red-500">*</span></label>
                      <div className="relative">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#b0a898] text-base font-medium pointer-events-none">$</span>
                        <input
                          type="text" name="monthlyIncome"
                          className={`${fieldClass('monthlyIncome')} pl-8`}
                          placeholder="5,000"
                          style={{ fontFamily: 'Montserrat, sans-serif' }}
                          onBlur={(e) => handleBlur('monthlyIncome', e.target.value)}
                          onChange={(e) => handleChange('monthlyIncome', e.target.value)}
                        />
                      </div>
                      <ErrorMsg msg={touched.monthlyIncome ? errors.monthlyIncome || '' : ''} />
                    </div>
                  </div>

                  {/* Time at Employer */}
                  <div>
                    <label className={labelClass} style={{ fontFamily: 'Montserrat, sans-serif' }}>Time with Employer <span className="text-red-500">*</span></label>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="relative">
                        <select
                          name="employerYears"
                          className={selectClass('employerYears')}
                          style={{ fontFamily: 'Montserrat, sans-serif' }}
                          onBlur={(e) => handleBlur('employerYears', e.target.value)}
                          onChange={(e) => handleChange('employerYears', e.target.value)}
                        >
                          <option value="">Years</option>
                          {Array.from({ length: 31 }, (_, i) => i).map((y) => (
                            <option key={y} value={y}>{y} {y === 1 ? 'Year' : 'Years'}</option>
                          ))}
                        </select>
                        <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-[#b0a898] pointer-events-none text-sm"></i>
                      </div>
                      <div className="relative">
                        <select
                          name="employerMonths"
                          className={selectClass('employerMonths')}
                          style={{ fontFamily: 'Montserrat, sans-serif' }}
                          onBlur={(e) => handleBlur('employerMonths', e.target.value)}
                          onChange={(e) => handleChange('employerMonths', e.target.value)}
                        >
                          <option value="">Months</option>
                          {Array.from({ length: 12 }, (_, i) => i).map((m) => (
                            <option key={m} value={m}>{m} {m === 1 ? 'Month' : 'Months'}</option>
                          ))}
                        </select>
                        <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-[#b0a898] pointer-events-none text-sm"></i>
                      </div>
                    </div>
                    <ErrorMsg msg={touched.employerYears ? errors.employerYears || '' : ''} />
                  </div>
                </div>
              </div>

              {/* ── Trade-In Vehicle ── */}
              <div id="section-tradein">
                <div className="flex items-center gap-3 mb-4">
                  <div className="h-px flex-1 bg-[#e0d8cc]"></div>
                  <span className="text-[#C9A84C] text-[10px] tracking-[0.3em] uppercase whitespace-nowrap font-bold" style={{ fontFamily: 'Montserrat, sans-serif' }}>Trade-In Vehicle</span>
                  <div className="h-px flex-1 bg-[#e0d8cc]"></div>
                </div>
                <div
                  onClick={() => setShowTradeIn(!showTradeIn)}
                  className={`flex items-center justify-between gap-4 cursor-pointer rounded-lg px-5 py-5 sm:py-4 border-2 transition-all duration-300 ${showTradeIn ? 'bg-[#fdf8ee] border-[#C9A84C] shadow-md' : 'bg-[#f5f2ed] border-[#d1c9b8] hover:border-[#C9A84C]/60 hover:bg-[#f0ece4]'}`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-11 h-11 sm:w-10 sm:h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${showTradeIn ? 'bg-[#C9A84C]/20' : 'bg-[#e0d8cc]'}`}>
                      <i className={`ri-exchange-line text-xl sm:text-lg ${showTradeIn ? 'text-[#C9A84C]' : 'text-[#6b5e4e]'}`}></i>
                    </div>
                    <div>
                      <p className="text-sm font-bold tracking-wide text-[#1a1a1a]" style={{ fontFamily: 'Montserrat, sans-serif' }}>I Have a Trade-In</p>
                      <p className="text-xs mt-0.5 text-[#6b5e4e]" style={{ fontFamily: 'Montserrat, sans-serif' }}>Optional — may reduce your monthly payments</p>
                    </div>
                  </div>
                  <div className={`relative w-14 h-7 sm:w-12 sm:h-6 rounded-full flex-shrink-0 transition-all duration-300 ${showTradeIn ? 'bg-[#C9A84C]' : 'bg-[#c8bfb0]'}`}>
                    <div className={`absolute top-1 w-5 h-5 sm:w-4 sm:h-4 bg-white rounded-full shadow transition-all duration-300 ${showTradeIn ? 'left-8 sm:left-7' : 'left-1'}`}></div>
                  </div>
                </div>

                {/* Expandable trade-in fields */}
                <div
                  className="overflow-hidden transition-all duration-500 ease-in-out"
                  style={{
                    maxHeight: showTradeIn ? '2000px' : '0',
                    opacity: showTradeIn ? 1 : 0,
                  }}
                >
                  <div className="mt-4 rounded-lg border border-[#d1c9b8] bg-[#f9f7f4] p-4 sm:p-5 space-y-6 sm:space-y-8">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 flex items-center justify-center bg-[#C9A84C] rounded-full flex-shrink-0">
                        <i className="ri-car-line text-white text-xs"></i>
                      </div>
                      <span className="text-xs font-bold text-[#4a4035] tracking-widest uppercase" style={{ fontFamily: 'Montserrat, sans-serif' }}>Trade-In Vehicle</span>
                    </div>

                    {/* Trade-In Vehicle Information */}
                    <div>
                      <p className="text-[10px] font-bold text-[#C9A84C] tracking-[0.25em] uppercase mb-4 border-b border-[#e0d8cc] pb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>Vehicle Details</p>
                      <div className="space-y-4">
                        {/* Year + Make + Model — stacked on mobile, one row on sm+ */}
                        <div className="grid grid-cols-1 sm:grid-cols-[1fr_60px_1fr] gap-3">
                          <div>
                            <label className={labelClass} style={{ fontFamily: 'Montserrat, sans-serif' }}>Year</label>
                            <input type="text" name="tradeInYear" className={fieldClass('tradeInYear')} placeholder="2020" maxLength={4} style={{ fontFamily: 'Montserrat, sans-serif' }} />
                          </div>
                          <div>
                            <label className={labelClass} style={{ fontFamily: 'Montserrat, sans-serif' }}>Make</label>
                            <input type="text" name="tradeInMake" className={fieldClass('tradeInMake')} placeholder="Toyota" style={{ fontFamily: 'Montserrat, sans-serif' }} />
                          </div>
                          <div>
                            <label className={labelClass} style={{ fontFamily: 'Montserrat, sans-serif' }}>Model</label>
                            <input type="text" name="tradeInModel" className={fieldClass('tradeInModel')} placeholder="Camry" style={{ fontFamily: 'Montserrat, sans-serif' }} />
                          </div>
                        </div>

                        {/* Mileage + Loan Balance */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <label className={labelClass} style={{ fontFamily: 'Montserrat, sans-serif' }}>Mileage</label>
                            <input type="text" name="tradeInMileage" className={fieldClass('tradeInMileage')} placeholder="45,000" style={{ fontFamily: 'Montserrat, sans-serif' }} />
                          </div>
                          <div>
                            <label className={labelClass} style={{ fontFamily: 'Montserrat, sans-serif' }}>Outstanding Loan Balance</label>
                            <div className="relative">
                              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#b0a898] text-base font-medium pointer-events-none">$</span>
                              <input type="text" name="tradeInLoanBalance" className={`${fieldClass('tradeInLoanBalance')} pl-8`} placeholder="0" style={{ fontFamily: 'Montserrat, sans-serif' }} />
                            </div>
                          </div>
                        </div>

                        {/* VIN */}
                        <div>
                          <label className={labelClass} style={{ fontFamily: 'Montserrat, sans-serif' }}>VIN (optional)</label>
                          <input type="text" name="tradeInVin" className={fieldClass('tradeInVin')} placeholder="1HGBH41JXMN109186" style={{ fontFamily: 'Montserrat, sans-serif' }} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* ── Co-Applicant ── */}
              <div id="section-co-applicant">
                <div className="flex items-center gap-3 mb-4">
                  <div className="h-px flex-1 bg-[#e0d8cc]"></div>
                  <span className="text-[#C9A84C] text-[10px] tracking-[0.3em] uppercase whitespace-nowrap font-bold" style={{ fontFamily: 'Montserrat, sans-serif' }}>Co-Applicant</span>
                  <div className="h-px flex-1 bg-[#e0d8cc]"></div>
                </div>
                <div
                  onClick={() => setShowCoApplicant(!showCoApplicant)}
                  className={`flex items-center justify-between gap-4 cursor-pointer rounded-lg px-5 py-5 sm:py-4 border-2 transition-all duration-300 ${showCoApplicant ? 'bg-[#fdf8ee] border-[#C9A84C] shadow-md' : 'bg-[#f5f2ed] border-[#d1c9b8] hover:border-[#C9A84C]/60 hover:bg-[#f0ece4]'}`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-11 h-11 sm:w-10 sm:h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${showCoApplicant ? 'bg-[#C9A84C]/20' : 'bg-[#e0d8cc]'}`}>
                      <i className={`ri-user-add-line text-xl sm:text-lg ${showCoApplicant ? 'text-[#C9A84C]' : 'text-[#6b5e4e]'}`}></i>
                    </div>
                    <div>
                      <p className="text-sm font-bold tracking-wide text-[#1a1a1a]" style={{ fontFamily: 'Montserrat, sans-serif' }}>Add a Co-Applicant</p>
                      <p className="text-xs mt-0.5 text-[#6b5e4e]" style={{ fontFamily: 'Montserrat, sans-serif' }}>Optional — may improve approval chances</p>
                    </div>
                  </div>
                  <div className={`relative w-14 h-7 sm:w-12 sm:h-6 rounded-full flex-shrink-0 transition-all duration-300 ${showCoApplicant ? 'bg-[#C9A84C]' : 'bg-[#c8bfb0]'}`}>
                    <div className={`absolute top-1 w-5 h-5 sm:w-4 sm:h-4 bg-white rounded-full shadow transition-all duration-300 ${showCoApplicant ? 'left-8 sm:left-7' : 'left-1'}`}></div>
                  </div>
                </div>

                {/* Expandable co-applicant fields */}
                <div
                  className="overflow-hidden transition-all duration-500 ease-in-out"
                  style={{
                    maxHeight: showCoApplicant ? '2000px' : '0',
                    opacity: showCoApplicant ? 1 : 0,
                  }}
                >
                  <div className="mt-4 rounded-lg border border-[#d1c9b8] bg-[#f9f7f4] p-4 sm:p-5 space-y-6">
                    <p className="text-[10px] font-bold text-[#C9A84C] tracking-[0.25em] uppercase border-b border-[#e0d8cc] pb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>Personal Details</p>
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 sm:grid-cols-[1fr_60px_1fr] gap-3">
                        <div>
                          <label className={labelClass} style={{ fontFamily: 'Montserrat, sans-serif' }}>First Name</label>
                          <input type="text" name="co_firstName" className={fieldClass('co_firstName')} placeholder="Jane" style={{ fontFamily: 'Montserrat, sans-serif' }} />
                        </div>
                        <div>
                          <label className={labelClass} style={{ fontFamily: 'Montserrat, sans-serif' }}>MI</label>
                          <input type="text" name="co_middleInitial" maxLength={1} className={fieldClass('co_middleInitial')} placeholder="B" style={{ fontFamily: 'Montserrat, sans-serif' }} />
                        </div>
                        <div>
                          <label className={labelClass} style={{ fontFamily: 'Montserrat, sans-serif' }}>Last Name</label>
                          <input type="text" name="co_lastName" className={fieldClass('co_lastName')} placeholder="Doe" style={{ fontFamily: 'Montserrat, sans-serif' }} />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <label className={labelClass} style={{ fontFamily: 'Montserrat, sans-serif' }}>SSN</label>
                          <input type="text" name="co_ssn" className={fieldClass('co_ssn')} placeholder="XXX-XX-XXXX" maxLength={11} style={{ fontFamily: 'Montserrat, sans-serif' }} onChange={e => handleSSNChange(e, 'co_ssn')} />
                        </div>
                        <div>
                          <label className={labelClass} style={{ fontFamily: 'Montserrat, sans-serif' }}>Date of Birth</label>
                          <div className="grid grid-cols-3 gap-1.5">
                            <div className="relative">
                              <select name="co_dobMonth" className={`${selectClass('co_dobMonth')} pr-6 text-sm`} style={{ fontFamily: 'Montserrat, sans-serif' }} onBlur={(e) => handleBlur('co_dobMonth', e.target.value)} onChange={(e) => handleChange('co_dobMonth', e.target.value)}>
                                <option value="">MM</option>
                                {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => <option key={m} value={String(m).padStart(2, '0')}>{String(m).padStart(2, '0')}</option>)}
                              </select>
                              <i className="ri-arrow-down-s-line absolute right-1.5 top-1/2 -translate-y-1/2 text-[#b0a898] pointer-events-none text-sm"></i>
                            </div>
                            <div className="relative">
                              <select name="co_dobDay" className={`${selectClass('co_dobDay')} pr-6 text-sm`} style={{ fontFamily: 'Montserrat, sans-serif' }} onBlur={(e) => handleBlur('co_dobDay', e.target.value)} onChange={(e) => handleChange('co_dobDay', e.target.value)}>
                                <option value="">DD</option>
                                {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => <option key={d} value={String(d).padStart(2, '0')}>{String(d).padStart(2, '0')}</option>)}
                              </select>
                              <i className="ri-arrow-down-s-line absolute right-1.5 top-1/2 -translate-y-1/2 text-[#b0a898] pointer-events-none text-sm"></i>
                            </div>
                            <div className="relative">
                              <select name="co_dobYear" className={`${selectClass('co_dobYear')} pr-6 text-sm`} style={{ fontFamily: 'Montserrat, sans-serif' }} onBlur={(e) => handleBlur('co_dobYear', e.target.value)} onChange={(e) => handleChange('co_dobYear', e.target.value)}>
                                <option value="">YYYY</option>
                                {Array.from({ length: 80 }, (_, i) => new Date().getFullYear() - 18 - i).map((y) => <option key={y} value={y}>{y}</option>)}
                              </select>
                              <i className="ri-arrow-down-s-line absolute right-1.5 top-1/2 -translate-y-1/2 text-[#b0a898] pointer-events-none text-sm"></i>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <label className={labelClass} style={{ fontFamily: 'Montserrat, sans-serif' }}>Cell Phone</label>
                          <input type="tel" name="co_cellPhone" className={fieldClass('co_cellPhone')} placeholder="(305) 000-0000" style={{ fontFamily: 'Montserrat, sans-serif' }} onChange={e => handlePhoneChange(e, 'co_cellPhone')} />
                        </div>
                        <div>
                          <label className={labelClass} style={{ fontFamily: 'Montserrat, sans-serif' }}>E-Mail Address</label>
                          <input type="email" name="co_email" className={fieldClass('co_email')} placeholder="jane@example.com" style={{ fontFamily: 'Montserrat, sans-serif' }} />
                        </div>
                      </div>
                    </div>
                    <p className="text-[10px] font-bold text-[#C9A84C] tracking-[0.25em] uppercase border-b border-[#e0d8cc] pb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>Employment Details</p>
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <label className={labelClass} style={{ fontFamily: 'Montserrat, sans-serif' }}>Employer Name</label>
                          <input type="text" name="co_employerName" className={fieldClass('co_employerName')} placeholder="Acme Corp" style={{ fontFamily: 'Montserrat, sans-serif' }} />
                        </div>
                        <div>
                          <label className={labelClass} style={{ fontFamily: 'Montserrat, sans-serif' }}>Occupation</label>
                          <input type="text" name="co_occupation" className={fieldClass('co_occupation')} placeholder="Software Engineer" style={{ fontFamily: 'Montserrat, sans-serif' }} />
                        </div>
                      </div>
                      <div>
                        <label className={labelClass} style={{ fontFamily: 'Montserrat, sans-serif' }}>Monthly Income</label>
                        <div className="relative">
                          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#b0a898] text-base font-medium pointer-events-none">$</span>
                          <input type="text" name="co_monthlyIncome" className={`${fieldClass('co_monthlyIncome')} pl-8`} placeholder="5,000" style={{ fontFamily: 'Montserrat, sans-serif' }} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* ── Consent ── */}
              <div id="section-consent" className="border border-[#e0d8cc] rounded-lg p-5 sm:p-6 bg-[#fdf8ee]">
                <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-4">
                  <span className="text-[#6b5e4e] text-xs tracking-widest uppercase cursor-pointer hover:text-[#C9A84C] transition-colors font-semibold" style={{ fontFamily: 'Montserrat, sans-serif' }}>Privacy Notice</span>
                  <span className="text-[#c8bfb0]">|</span>
                  <span className="text-[#6b5e4e] text-xs tracking-widest uppercase cursor-pointer hover:text-[#C9A84C] transition-colors font-semibold" style={{ fontFamily: 'Montserrat, sans-serif' }}>Terms and Conditions</span>
                </div>
                <p className="text-[#5a4e40] text-xs sm:text-xs leading-relaxed mb-5" style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 400 }}>
                  By clicking submit, you agree to our Terms and authorize Paramount Motors to share your data with our lending partners for a credit check.
                </p>
                <label className="flex items-center gap-3 cursor-pointer group">
                  <div
                    onClick={() => setAgreed(!agreed)}
                    className={`w-6 h-6 sm:w-5 sm:h-4 flex-shrink-0 border-2 rounded flex items-center justify-center transition-all cursor-pointer ${agreed ? 'bg-[#C9A84C] border-[#C9A84C]' : 'border-[#c8bfb0] bg-white group-hover:border-[#C9A84C]'}`}
                  >
                    {agreed && <i className="ri-check-line text-white text-sm"></i>}
                  </div>
                  <span className="text-[#4a4035] text-sm sm:text-sm font-semibold tracking-wider" style={{ fontFamily: 'Montserrat, sans-serif' }}>I Agree</span>
                </label>
              </div>

              {submitError && (
                <div className="flex items-start gap-2.5 bg-red-50 border border-red-200 rounded-md px-4 py-3">
                  <i className="ri-error-warning-fill text-red-500 text-base flex-shrink-0 mt-0.5"></i>
                  <p className="text-red-600 text-xs font-medium" style={{ fontFamily: 'Montserrat, sans-serif' }}>{submitError}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting || !agreed}
                className="w-full bg-[#C9A84C] hover:bg-[#b8943e] text-white py-4 sm:py-3.5 min-h-[52px] sm:min-h-[48px] rounded-md text-sm sm:text-xs font-bold tracking-widest uppercase transition-all cursor-pointer whitespace-nowrap hover:scale-[1.01] transform disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-md"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                {isSubmitting ? <><i className="ri-loader-4-line animate-spin"></i>Submitting...</> : 'Submit Application'}
              </button>
            </form>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#080808] border-t border-white/5 py-8 sm:py-10 md:py-12 px-4 sm:px-8 lg:px-16">
        <div className="h-px bg-gradient-to-r from-transparent via-[#C9A84C] to-transparent mb-8 sm:mb-10 md:mb-12"></div>
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-5 sm:gap-6">
          <div className="flex items-center gap-2 sm:gap-3">
            <img
              src="https://static.readdy.ai/image/41574e0492e980fd88e2c6f8d59234d9/ff8f99c2c3d017ecd9846c9b47aca342.png"
              alt="Paramount Motors Logo"
              style={{ height: '3rem', width: 'auto' }}
              className="object-contain"
            />
          </div>
          <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6">
            {navItems.map((item) => (
              <Link key={item.name} to={item.path} className="text-xs text-white/40 hover:text-[#C9A84C] transition-colors cursor-pointer tracking-widest uppercase" style={{ fontFamily: 'Montserrat, sans-serif' }}>{item.name}</Link>
            ))}
          </div>
          <p className="text-xs text-white/20" style={{ fontFamily: 'Montserrat, sans-serif' }}>© 2025 Paramount Motors</p>
        </div>
      </footer>
    </div>
  );
}